import { SimpleListItem } from '@/components/ui/simple-list-item';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Radio, 
  Smartphone,
  MoreVertical,
  Cloud,
  CloudOff,
  RefreshCw,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatCurrency } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Product {
  id: string;
  name: string;
  description?: string;
  price_cents: number;
  is_active: boolean;
  product_type: 'gps_device' | 'm2m_chip';
  console_device_id?: string | null;
  console_synced_at?: string | null;
}

interface ProductSimpleListProps {
  products: Product[];
  productType: 'gps_device' | 'm2m_chip';
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onSync?: (product: Product) => void;
  isSyncing?: boolean;
}

export function ProductSimpleList({ 
  products, 
  productType,
  onEdit, 
  onDelete,
  onSync,
  isSyncing = false,
}: ProductSimpleListProps) {
  const isGPS = productType === 'gps_device';
  const Icon = isGPS ? Radio : Smartphone;
  const emptyMessage = isGPS ? 'Nenhum dispositivo GPS cadastrado' : 'Nenhum chip M2M cadastrado';

  if (products.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <Icon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{emptyMessage}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{isGPS ? 'Dispositivos GPS' : 'Chips M2M'}</span>
          <Badge variant="secondary">{products.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {products.map((product) => (
            <SimpleListItem
              key={product.id}
              icon={Icon}
              title={product.name}
              subtitle={product.description || (isGPS ? 'Dispositivo GPS' : 'Chip M2M')}
              rightContent={
                <div className="flex items-center gap-3">
                  {/* Preço */}
                  <span className="text-sm font-semibold text-primary">
                    {formatCurrency(product.price_cents / 100)}
                  </span>
                  
                  {/* Badge de sincronização */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {product.console_device_id ? (
                          <Badge variant="outline" className="text-green-600 border-green-600 bg-green-50">
                            <Cloud className="h-3 w-3 mr-1" />
                            Sincronizado
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-600 bg-amber-50">
                            <CloudOff className="h-3 w-3 mr-1" />
                            Não sincronizado
                          </Badge>
                        )}
                      </TooltipTrigger>
                      <TooltipContent>
                        {product.console_synced_at 
                          ? `Sincronizado em ${format(new Date(product.console_synced_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`
                          : 'Ainda não sincronizado com a Console'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  
                  {/* Badge de status */}
                  <Badge variant={product.is_active ? 'default' : 'outline'}>
                    {product.is_active ? 'Ativo' : 'Inativo'}
                  </Badge>
                  
                  {/* Menu de ações */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-popover">
                      <DropdownMenuItem onClick={() => onEdit(product)}>
                        Editar
                      </DropdownMenuItem>
                      {onSync && (
                        <DropdownMenuItem 
                          onClick={() => onSync(product)}
                          disabled={isSyncing}
                        >
                          <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                          Sincronizar
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => onDelete(product)}
                        className="text-destructive"
                      >
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              }
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
